'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { CalendarIcon, ClockIcon } from 'lucide-react';

import UserIcon from '@/components/icons/user';
import type { FanficData } from '@/features/fanfic-page/types/fanfic';
import { resolveFanficPublishedAt } from '@/features/fanfic-card/utils/resolve-fanfic-published-at.util';
import { parseTextWithLinks } from '../../utils/parseTextWithLinks.util';

export interface FanficInfoBarProps {
  fanfic: FanficData;
  timeToRead: string;
}

/**
 * Компонент FanficInfoBar - отображает статистику и мета-информацию о фанфике
 */
export function FanficInfoBar({ fanfic, timeToRead }: FanficInfoBarProps) {
  const [showAllTags, setShowAllTags] = useState<boolean>(false);
  const publishedAt = resolveFanficPublishedAt(fanfic);
  const publishedAtLabel = publishedAt ? new Date(publishedAt).toLocaleDateString('ru-RU') : null;

  return (
    <>
      {/* Desktop stats */}
      <div className="hidden min-[1000px]:flex items-center rounded-lg gap-4">
        <div className="flex-1 bg-[#F2F3F7] p-3 flex flex-col items-center rounded-xl">
          <span className="text-xs text-[#6e7384] mb-1">Читатели:</span>
          <div className="flex items-center gap-1">
            <UserIcon className="w-3.5 h-3.5 text-[#B4BACD]" />
            <span className="text-sm text-[#111827]">{fanfic.readersCount ?? 0}</span>
          </div>
        </div>
        {publishedAtLabel ? (
          <div className="flex-1 p-3 bg-[#F2F3F7] flex flex-col items-center rounded-xl">
            <span className="text-xs text-[#6e7384] mb-1">Дата публикации:</span>
            <div className="flex items-center gap-1">
              <CalendarIcon className="w-3.5 h-3.5 text-[#B4BACD]" />
              <span className="text-sm text-[#111827]">{publishedAtLabel}</span>
            </div>
          </div>
        ) : null}
        <div className="flex-1 p-3 bg-[#F2F3F7] flex flex-col items-center rounded-xl">
          <span className="text-xs text-[#6e7384] mb-1">Время:</span>
          <div className="flex items-center gap-1">
            <ClockIcon className="w-3.5 h-3.5 text-[#B4BACD]" />
            <span className="text-sm text-[#111827]">{timeToRead}</span>
          </div>
        </div>
      </div>

      {/* Mobile stats */}
      <div className="flex items-center rounded-lg gap-4 bg-[#F2F3F7] min-[1000px]:hidden">
        <div className="flex-1 p-3 flex flex-col items-center">
          <span className="text-xs text-[#6e7384] mb-1">Читатели:</span>
          <div className="flex items-center gap-1">
            <UserIcon className="w-3.5 h-3.5 text-[#B4BACD]" />
            <span className="text-sm text-[#111827]">{fanfic.readersCount ?? 0}</span>
          </div>
        </div>
        {publishedAtLabel ? (
          <>
            <div className="w-px h-[44px] bg-[#dee1ea]" />
            <div className="flex-1 p-3 flex flex-col items-center rounded-xl justify-center">
              <span className="text-center text-xs text-[#6e7384] mb-1">Дата публикации:</span>
              <div className="flex items-center gap-1">
                <CalendarIcon className="w-3.5 h-3.5 text-[#B4BACD]" />
                <span className="text-sm text-[#111827]">{publishedAtLabel}</span>
              </div>
            </div>
          </>
        ) : null}
        <div className="w-px h-[44px] bg-[#dee1ea]" />
        <div className="flex-1 p-3 flex flex-col items-center">
          <span className="text-xs text-[#6e7384] mb-1">Время:</span>
          <div className="flex items-center gap-1">
            <ClockIcon className="w-3.5 h-3.5 text-[#B4BACD]" />
            <span className="text-sm text-[#111827]">{timeToRead}</span>
          </div>
        </div>
      </div>

      {/* Pairings and characters */}
      <div>
        <div className="text-[#6E7384] mb-3">Пэйринг и персонажи:</div>
        <div className="flex flex-wrap gap-2">
          {fanfic.pairings && fanfic.pairings.length > 0
            ? fanfic.pairings.map((pairing, index) => {
                // @ts-expect-error Pairing type does not include characters from the API
                const pairingTitle = pairing?.characters.map((el: any) => el?.name).join('/');

                if (!pairingTitle || pairingTitle.trim() === '') {
                  return null;
                }

                return pairing.permanentUrl ? (
                  <Link
                    key={`pairing_${pairing.id}_${index}`}
                    href={pairing.permanentUrl}
                    className="border-2 border-[#E5E7EB] text-[#111827] px-3 py-1 rounded-lg text-sm bg-[#F8F9FA] hover:border-[#505CFF] hover:text-[#505CFF] transition-colors"
                  >
                    {pairingTitle}
                  </Link>
                ) : (
                  <span
                    key={`pairing_${pairing.id}_${index}`}
                    className="border-2 border-[#E5E7EB] text-[#111827] px-3 py-1 rounded-lg text-sm bg-[#F8F9FA]"
                  >
                    {pairingTitle}
                  </span>
                );
              })
            : null}
          {fanfic.characters && fanfic.characters.length > 0
            ? fanfic.characters.map((character, index) => {
                const characterName = character.name || character.title;

                if (!characterName || characterName.trim() === '') {
                  return null;
                }

                return character.permanentUrl ? (
                  <Link
                    key={`character_${character.id}_${index}`}
                    href={character.permanentUrl}
                    className="border-2 border-[#E5E7EB] text-[#111827] px-3 py-1 rounded-lg text-sm bg-[#F8F9FA] hover:border-[#505CFF] hover:text-[#505CFF] transition-colors"
                  >
                    {characterName}
                  </Link>
                ) : (
                  <span
                    key={`character_${character.id}_${index}`}
                    className="border-2 border-[#E5E7EB] text-[#111827] px-3 py-1 rounded-lg text-sm bg-[#F8F9FA]"
                  >
                    {characterName}
                  </span>
                );
              })
            : null}
          {(!fanfic.pairings || fanfic.pairings.length === 0) &&
            (!fanfic.characters || fanfic.characters.length === 0) && (
              <span className="text-[#6e7384] text-sm">Пэйринги не указаны</span>
            )}
        </div>
      </div>

      {/* Tags */}
      <div className="space-y-2">
        <div className="font-medium text-[#6E7384]">Метки:</div>
        <div className="flex flex-wrap gap-2">
          {fanfic.tags && fanfic.tags.length > 0 ? (
            <>
              {fanfic.tags
                .slice(0, showAllTags ? fanfic.tags.length : 10)
                .map((tag, index: number) =>
                  tag.permanentUrl ? (
                    <Link
                      key={`${tag.title}_${index}`}
                      href={tag.permanentUrl}
                      className="border-2 border-[#E5E7EB] text-[#111827] px-2 py-1 rounded-lg text-sm hover:border-[#1d4ed8] hover:text-[#1d4ed8] transition-colors"
                    >
                      {tag.title}
                    </Link>
                  ) : (
                    <span
                      key={`${tag.title}_${index}`}
                      className="border-2 border-[#E5E7EB] text-[#111827] px-2 py-1 rounded-lg text-sm"
                    >
                      {tag.title}
                    </span>
                  ),
                )}
              {fanfic.tags.length > 10 && !showAllTags && (
                <button
                  type="button"
                  className="text-sm hover:opacity-80 transition-opacity"
                  onClick={() => setShowAllTags(true)}
                  style={{ color: '#505CFF', fontWeight: 500 }}
                >
                  Показать ещё...
                </button>
              )}
            </>
          ) : (
            <span className="text-[#6e7384] text-sm">Метки не указаны</span>
          )}
        </div>
      </div>

      {/* Full description */}
      <div className="space-y-6 hidden min-[1000px]:block">
        <div>
          <div className="text-[#6E7384]">Описание:</div>
          <div className="font-medium text-[#111827] space-y-4 leading-[120%] tracking-[-1%]">
            <p>
              {parseTextWithLinks(
                fanfic.description || fanfic.content || 'Описание пока отсутствует.',
              )}
            </p>
          </div>
        </div>
        {fanfic.note && fanfic.note.trim() !== '' && (
          <div>
            <div className="text-[#6E7384]">Примечание:</div>
            <div className="font-medium text-[#111827] space-y-4 leading-[120%] tracking-[-1%]">
              <p>{parseTextWithLinks(fanfic.note)}</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
